import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Crown, Lock, Sparkles, Brain, Utensils, Watch, Camera, BarChart3, Dumbbell, Footprints, Apple } from 'lucide-react';

const ALL_FEATURES = [
  { icon: Dumbbell, name: 'Workout Logging', description: 'Log your training sessions' },
  { icon: Apple, name: 'Nutrition Tracking', description: 'Track calories & macros' },
  { icon: Footprints, name: 'Walking Tracker', description: 'GPS tracking & step counting' },
  { icon: Brain, name: 'AI Workout Planner', description: 'Personalized AI-generated plans' },
  { icon: Utensils, name: 'AI Meal Planning', description: 'Smart nutrition recommendations' },
  { icon: Watch, name: 'Wearable Sync', description: 'Fitbit, Polar, Withings integration' },
  { icon: Camera, name: 'Restaurant Scanner', description: 'AI-powered menu analysis' },
  { icon: BarChart3, name: 'Statistics & Progress', description: 'Detailed progress analytics' },
];

export default function PaywallModal({ isOpen, onClose, onSubscribe, daysRemaining, isExpired }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700 max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 p-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full w-fit">
            {isExpired ? (
              <Lock className="h-8 w-8 text-white" />
            ) : (
              <Crown className="h-8 w-8 text-white" />
            )}
          </div>
          <DialogTitle className="text-2xl text-white">
            {isExpired ? 'Your Trial Has Ended' : 'Upgrade to Premium'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {isExpired 
              ? 'Subscribe to continue using the app'
              : `${daysRemaining} days left in your free trial`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 my-4">
          <p className="text-sm text-slate-300 text-center">
            {isExpired 
              ? 'You can view your existing data, but all logging features are locked until you subscribe.'
              : 'Unlock all features with a subscription:'
            }
          </p>
          
          <div className="grid grid-cols-2 gap-2">
            {ALL_FEATURES.map((feature, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-2 p-2 rounded-lg ${
                  isExpired ? 'bg-slate-700/50 opacity-60' : 'bg-slate-700/30'
                }`}
              >
                <feature.icon className={`h-4 w-4 flex-shrink-0 ${isExpired ? 'text-slate-500' : 'text-amber-400'}`} />
                <span className="text-xs text-slate-300 truncate">{feature.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <Button 
            onClick={onSubscribe}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-6"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            View Subscription Plans
          </Button>
          
          {!isExpired && (
            <Button 
              variant="ghost" 
              onClick={onClose}
              className="w-full text-slate-400 hover:text-white"
            >
              Continue with Trial
            </Button>
          )}
          
          {isExpired && (
            <Button 
              variant="ghost" 
              onClick={onClose}
              className="w-full text-slate-400 hover:text-white"
            >
              View Data Only
            </Button>
          )}
        </div>

        <p className="text-xs text-slate-500 text-center mt-2">
          Cancel anytime. Your data is always yours.
        </p>
      </DialogContent>
    </Dialog>
  );
}

// Export feature list for use elsewhere
export const FEATURE_KEYS = [
  'workout',
  'nutrition',
  'walking',
  'ai_planner',
  'meal_planning', 
  'wearables',
  'restaurant_scanner',
  'statistics',
];
